/**
 * T-2 · Valider une évaluation produite par l'IA — la colonne vertébrale.
 *
 * Deux gestes, jamais trois (§10.5) : **confirmer** d'un clic, ou **corriger**
 * en choisissant un palier et en écrivant une ligne de motif. Toute étape
 * supplémentaire tue le KPI des vingt minutes économisées par élève et par
 * semaine.
 *
 * Trois invariants tenus à l'écran :
 *  - **la correction se fait en CHOISISSANT un palier**, jamais en tapant un
 *    nombre. Les cinq paliers nommés du contrat sont les seules valeurs que
 *    l'API accepte (`z.enum(NIVEAUX_MAITRISE)`), et le champ est un `select` ;
 *  - **la confiance du moteur est qualifiée en mots** (« confiance moyenne »),
 *    jamais rendue en nombre : un tuteur ne décide pas sur « 0,62 » ;
 *  - **l'effet est annoncé, et la trace est rendue à la famille.** Après la
 *    décision, l'écran affiche `effetVisible` et `revuePar` tels que l'API les
 *    rédige — « Revu par Samuel, tuteur » est la preuve visible qu'un humain
 *    regarde, et c'est la seule réponse crédible à « au fond, c'est juste une
 *    IA ».
 *
 * Le traitement est EN FILE : on enchaîne l'item suivant sans repasser par la
 * liste. Le `commentaire` facultatif est une annotation de copie, visible de
 * l'élève et de son parent, sans réponse possible — Silex ne porte aucune
 * messagerie, et l'écran le dit.
 */
import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  LIBELLES_DIMENSION,
  NIVEAUX_MAITRISE,
  type EvaluationAValiderDto,
  type NiveauMaitrise,
  type RevueEvaluationResponse,
} from '@silex/shared';

import { api } from '../api/client';
import { EmptyState } from '../components/EmptyState';
import { Chargement, ErreurRessource } from '../components/EtatRessource';
import { LegendePalier, PalierMaitrise } from '../components/PalierMaitrise';
import {
  formatDateHeure,
  formatDuree,
  libelleConfiance,
  libelleNiveauScolaire,
  libellePalier,
  matiereConsole,
} from '../lib/tuteur-format';
import { messageErreur, useAsyncRechargeable } from '../lib/useAsync';
import { useTuteur } from '../tuteur/TuteurContext';

export function RevueEvaluationPage() {
  const { evaluationId } = useParams<'evaluationId'>();
  const navigate = useNavigate();
  const { rechargerFile } = useTuteur();

  const loader = useMemo(() => () => api.getEvaluationsAValider(), []);
  const { resource: file, recharger } = useAsyncRechargeable(loader);

  const [resultat, setResultat] = useState<RevueEvaluationResponse | null>(null);

  const apresDecision = useCallback(
    (reponse: RevueEvaluationResponse) => {
      setResultat(reponse);
      // Le serveur recalcule la file du matin à partir des faits : une
      // évaluation revue en sort d'elle-même. On la redemande plutôt que de
      // deviner ce qu'elle contiendrait.
      rechargerFile();
      recharger();
    },
    [rechargerFile, recharger],
  );

  if (file.status === 'loading') {
    return (
      <div className="content__inner">
        <Chargement quoi="la file de validation" />
      </div>
    );
  }
  if (file.status === 'error') {
    return (
      <div className="content__inner">
        <ErreurRessource message={file.message} />
      </div>
    );
  }

  const items = file.data.evaluations;
  const index = items.findIndex((e) => e.evaluationId === evaluationId);
  const courant = index >= 0 ? items[index] : undefined;
  const suivant = items.find((e) => e.evaluationId !== evaluationId);

  if (resultat) {
    return (
      <ApresRevue
        resultat={resultat}
        suivantId={suivant?.evaluationId ?? null}
        onSuivant={() => {
          setResultat(null);
          navigate(suivant ? `/validation/${suivant.evaluationId}` : '/validation');
        }}
      />
    );
  }

  if (!courant) {
    return (
      <div className="content__inner">
        <p className="breadcrumb">
          <Link to="/ma-journee">Ma journée</Link> / <Link to="/validation">À valider</Link>
        </p>
        <EmptyState title="Cette évaluation n'attend plus de verdict">
          Elle a été revue, ou elle n'est plus dans votre périmètre. La file de validation est
          recalculée à chaque ouverture : elle ne garde pas d'item fantôme.
        </EmptyState>
      </div>
    );
  }

  return (
    <Revue
      evaluation={courant}
      position={index + 1}
      total={items.length}
      onDecision={apresDecision}
    />
  );
}

/* ------------------------------------------------------------ l'écran de revue */

function Revue({
  evaluation: ev,
  position,
  total,
  onDecision,
}: {
  evaluation: EvaluationAValiderDto;
  position: number;
  total: number;
  onDecision: (r: RevueEvaluationResponse) => void;
}) {
  const [commentaire, setCommentaire] = useState('');
  const [palierCorrige, setPalierCorrige] = useState<NiveauMaitrise>(
    ev.niveauMaitrisePropose ?? 'en_consolidation',
  );
  const [motif, setMotif] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const soumettre = useCallback(
    async (decision: 'confirme' | 'corrige') => {
      setErreur(null);
      setEnvoi(true);
      try {
        const reponse = await api.revoirEvaluation(ev.evaluationId, {
          decision,
          ...(decision === 'corrige' ? { niveauMaitrise: palierCorrige, motif } : {}),
          ...(commentaire.trim() ? { commentaire } : {}),
        });
        onDecision(reponse);
      } catch (err) {
        setErreur(messageErreur(err));
      } finally {
        setEnvoi(false);
      }
    },
    [ev.evaluationId, palierCorrige, motif, commentaire, onDecision],
  );

  const correctionPrete = motif.trim().length > 0;

  return (
    <div className="content__inner">
      <div className="dash-topbar">
        <div>
          <p className="breadcrumb">
            <Link to="/ma-journee">Ma journée</Link> / <Link to="/validation">À valider</Link> /{' '}
            {ev.elevePrenom}
          </p>
          <h1>
            {ev.savoir ? ev.savoir.enonce : 'Savoir non résolu'} — {ev.elevePrenom}
          </h1>
          <p className="page-sub">
            {ev.savoir ? `${ev.savoir.code} · ${matiereConsole(ev.savoir.matiere)} · ` : ''}
            {libelleNiveauScolaire(ev.eleveNiveauScolaire)} · évaluation soumise le{' '}
            {formatDateHeure(ev.soumiseLe)}
          </p>
        </div>
        <div className="stack-sm topbar-droite">
          <div className="stepper" aria-label={`Item ${position} sur ${total}`}>
            {Array.from({ length: total }, (_, i) => (
              <span
                key={i}
                className={
                  i + 1 === position ? 'st is-current' : i + 1 < position ? 'st is-done' : 'st'
                }
                aria-hidden="true"
              >
                <span className="n">{i + 1}</span>
              </span>
            ))}
          </div>
          <span className="chip-time">
            Item {position} sur {total} · {formatDuree(ev.dureeEstimeeMinutes)}
          </span>
        </div>
      </div>

      {erreur ? <ErreurRessource message={erreur} /> : null}

      <div className="tuteur-grid">
        <div className="stack">
          <section className="card">
            <div className="row-between">
              <h3>Le travail de {ev.elevePrenom}</h3>
              {ev.nbItems !== null ? (
                <span className="chip-time">
                  {ev.nbItemsJustes ?? 0} juste{(ev.nbItemsJustes ?? 0) > 1 ? 's' : ''} sur{' '}
                  {ev.nbItems}
                </span>
              ) : null}
            </div>
            {ev.forces.length === 0 && ev.lacunes.length === 0 ? (
              <p className="note-line">
                Ni forces ni lacunes renseignées, et le détail des items n'est pas servi à la
                console.
              </p>
            ) : (
              <>
                {ev.forces.length > 0 ? (
                  <>
                    <h4>Ce qui tient</h4>
                    <ul>
                      {ev.forces.map((f) => (
                        <li key={f}>{f}</li>
                      ))}
                    </ul>
                  </>
                ) : null}
                {ev.lacunes.length > 0 ? (
                  <>
                    <h4>Ce qui ne tient pas</h4>
                    <ul>
                      {ev.lacunes.map((l) => (
                        <li key={l}>{l}</li>
                      ))}
                    </ul>
                  </>
                ) : null}
                <p className="note-line">
                  Forces et lacunes sont rédigées par le producteur de l'évaluation et affichées
                  telles quelles.
                </p>
              </>
            )}
          </section>

          <section className="card">
            <div className="row-between">
              <h3>Commentaire sur ce travail</h3>
              <span className="badge badge--new">Facultatif</span>
            </div>
            <p className="card-sub">
              Attaché à cette évaluation. Visible de {ev.elevePrenom} et de son parent. Sans réponse
              possible : ce n'est pas une conversation.
            </p>
            <label className="field field--bloc">
              <span className="lbl sr-only">Commentaire attaché à l'évaluation</span>
              <textarea
                className="control"
                rows={3}
                value={commentaire}
                onChange={(e) => setCommentaire(e.target.value)}
                placeholder="Ce que vous avez vu dans ce travail."
              />
            </label>
            <p className="note-line">
              Une annotation de copie, pas un message : elle se pose sur un travail et n'ouvre aucun
              fil.
            </p>
          </section>

          <section className="card">
            <h3>Ce savoir chez {ev.elevePrenom}, jusqu'ici</h3>
            {ev.niveauMaitriseActuel ? (
              <div className="savoir-row">
                <div className="savoir-row__main">
                  {/* LOT 1.4 (BD-08) — « PALIER ACTUEL » sans nom de dimension.
                      Le tuteur lisait « En découverte », cliquait
                      « Confirmer "en découverte" » — un geste que l'écran
                      présentait comme sans effet — et la dimension `calcul` de
                      Thomas passait de « en consolidation » à « en découverte ».
                      Un palier sans sa dimension se lit comme un palier de
                      savoir, et il n'existe pas de palier de savoir. */}
                  <span className="savoir-row__matiere">
                    Palier actuel · {LIBELLES_DIMENSION[ev.dimension].toLowerCase()}
                  </span>
                  <span className="savoir-row__enonce">
                    {ev.savoir ? ev.savoir.enonce : 'Savoir non résolu'}
                  </span>
                </div>
                <div className="savoir-row__meta">
                  <PalierMaitrise niveau={ev.niveauMaitriseActuel} />
                </div>
              </div>
            ) : (
              <p className="note-line">
                Aucun palier n'est encore enregistré sur ce savoir : cette évaluation serait la
                première observation.
              </p>
            )}
            <p className="note-line">
              Votre décision porte sur <b>{LIBELLES_DIMENSION[ev.dimension].toLowerCase()}</b>, et
              sur cette mesure seule. L'autre lecture d'un savoir visuel — savoir lire la figure —
              ne bouge pas ici. <b>Confirmer ne fait jamais reculer {ev.elevePrenom}</b> : si la
              proposition est plus basse que le palier déjà atteint, c'est le palier atteint qui
              reste. Seule une correction motivée peut abaisser une mesure.
            </p>
            <p className="note-line">
              <Link to={`/eleves/${ev.eleveId}`}>Ouvrir la fiche de {ev.elevePrenom}</Link> pour
              l'historique complet — sans quitter cette file, elle vous attend.
            </p>
          </section>
        </div>

        <div className="stack">
          <section className="card">
            <span className="stage-label">Proposé par l'IA</span>
            {ev.niveauMaitrisePropose ? (
              <PalierMaitrise niveau={ev.niveauMaitrisePropose} />
            ) : (
              <p className="note-line">
                Le moteur n'a proposé aucun palier. Confirmer enregistrera votre revue sans toucher
                au profil de maîtrise — l'API le dit explicitement dans sa réponse.
              </p>
            )}
            <LegendePalier />
            <hr className="sep-soft" />
            <span className="stage-label">L'appui</span>
            <div className="list list--flush">
              {ev.nbItems !== null ? (
                <div className="li">
                  <div className="li-main">
                    <div className="li-title">
                      {ev.nbItems} items, {ev.nbItemsJustes ?? 0} justes
                    </div>
                    <div className="li-sub">Compte brut de l'évaluation, sans moyenne.</div>
                  </div>
                </div>
              ) : null}
              {ev.confiance ? (
                <div className="li">
                  <div className="li-main">
                    <div className="li-title">{libelleConfiance(ev.confiance)}</div>
                    <div className="li-sub">
                      La confiance du moteur est qualifiée en mots, jamais rendue en nombre.
                    </div>
                  </div>
                </div>
              ) : null}
              {ev.niveauMaitriseActuel ? (
                <div className="li">
                  <div className="li-main">
                    <div className="li-title">
                      Palier précédent : {libellePalier(ev.niveauMaitriseActuel).toLowerCase()}
                    </div>
                    <div className="li-sub">
                      Sur {LIBELLES_DIMENSION[ev.dimension].toLowerCase()}. Une confirmation ne peut
                      que le maintenir ou le faire monter.
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </section>

          <section className="card card--decision">
            <span className="stage-label">Votre décision</span>
            <button
              type="button"
              className="btn btn--primary btn--block"
              disabled={envoi}
              onClick={() => void soumettre('confirme')}
            >
              {ev.niveauMaitrisePropose
                ? `Confirmer « ${libellePalier(ev.niveauMaitrisePropose).toLocaleLowerCase('fr')} »`
                : 'Confirmer la lecture du moteur'}
            </button>
            <p className="note-line note-line--centre">
              Un clic. C'est le geste attendu dans la majorité des cas.
            </p>

            <hr className="sep-soft" />

            <label className="field field--bloc">
              <span className="lbl">Ou corriger le palier</span>
              <select
                className="control"
                value={palierCorrige}
                onChange={(e) => setPalierCorrige(e.target.value as NiveauMaitrise)}
              >
                {NIVEAUX_MAITRISE.map((niveau) => (
                  <option key={niveau} value={niveau}>
                    {libellePalier(niveau)}
                    {niveau === ev.niveauMaitrisePropose ? " — proposé par l'IA" : ''}
                  </option>
                ))}
              </select>
            </label>
            <label className="field field--bloc">
              <span className="lbl">Motif de la correction</span>
              <input
                className="control"
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                placeholder="Une ligne suffit — elle est journalisée."
              />
            </label>
            <button
              type="button"
              className="btn btn--ghost btn--block"
              disabled={envoi || !correctionPrete}
              onClick={() => void soumettre('corrige')}
            >
              Corriger et passer au suivant
            </button>
            <p className="note-line">
              Une correction demande un palier — un des cinq, choisi, jamais saisi — et un motif.
              L'API refuse toute autre forme.
            </p>
          </section>

          <section className="card card--calm">
            <h3>Ce que votre décision produit</h3>
            <div className="list list--flush">
              <div className="li">
                <div className="li-main">
                  <div className="li-sub li-sub--fort">
                    Le profil de maîtrise de {ev.elevePrenom} est réalimenté immédiatement. Sa
                    prochaine tâche du jour en tient compte.
                  </div>
                </div>
              </div>
              <div className="li">
                <div className="li-main">
                  <div className="li-sub li-sub--fort">
                    {ev.elevePrenom} et son parent voient apparaître « Revu par… » sur cette
                    évaluation. C'est la preuve visible qu'un humain regarde.
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------- après la décision */

/**
 * L'unité se termine par « fait ». L'effet et la trace rendue à la famille sont
 * les phrases de l'API (`effetVisible`, `revuePar`), affichées telles quelles.
 */
function ApresRevue({
  resultat,
  suivantId,
  onSuivant,
}: {
  resultat: RevueEvaluationResponse;
  suivantId: string | null;
  onSuivant: () => void;
}) {
  return (
    <div className="content__inner">
      <div className="page-head">
        <div>
          <p className="breadcrumb">
            <Link to="/ma-journee">Ma journée</Link> / <Link to="/validation">À valider</Link>
          </p>
          <h1>C'est fait.</h1>
        </div>
      </div>

      <section className="card callout--ok" role="status">
        <h3>
          {resultat.decision === 'confirme' ? 'Palier confirmé' : 'Palier corrigé'}
          {resultat.niveauMaitriseApres
            ? ` — ${libellePalier(resultat.niveauMaitriseApres).toLocaleLowerCase('fr')}`
            : ''}
        </h3>
        <p className="lede">{resultat.effetVisible}</p>
        <p className="note-line">
          Ce que la famille voit : « Revu par {resultat.revuePar}. »
          {resultat.maitriseMiseAJour
            ? ''
            : " Le savoir n'a pas pu être résolu : la revue est enregistrée, le profil de maîtrise reste inchangé."}
        </p>
        <div className="row wrap">
          <button type="button" className="btn btn--primary" onClick={onSuivant}>
            {suivantId ? 'Passer au suivant' : 'Revenir à la file'}
          </button>
          <Link className="btn btn--quiet" to="/ma-journee">
            Revenir à ma journée
          </Link>
        </div>
      </section>
    </div>
  );
}
